import React, {Fragment} from 'react';
import {useNavigate} from 'react-router';
import {cn, cva, InlineSvg, RsnDialog, RsnIconWidget, RsnSkeleton, styles, useBoolean} from '~/library/ui-kit';
import rightArrow from '~/shared/legacy_res/img/svg/arrow-right-dark.svg';
import sheetIcon from '~/shared/legacy_res/img/svg/sheet.svg';
import trashBoxIcon from '~/shared/legacy_res/img/svg/trash-box.svg';
import arrowRightIcon from '../../../../../../../static/icons/arrow-right-s-line.svg';
import {CardActionView, CardListItemView} from '../../uiSchemas';
import ApproveCardDeleteModal from './ApproveCardDeleteModal/ApproveCardDeleteModal';

export function CardListItem(props: CardListItemView) {
    const {title, iconSrc, statusColor, statusText} = props;
    const navigate = useNavigate();

    // TODO: use global modals
    const [showingMenu, setShowingMenu] = useBoolean(false);
    const [isApproveCardDeleteOpen, setApproveCardDeleteOpen] = useBoolean(false);

    const getCardActionView = (card: CardListItemView) => {
        const actionViews: CardActionView[] = [];
        if (card.needToVerify && !card.isDisabled) {
            actionViews.push({
                icon: 'verify',
                label: 'Verify',
                color: 'normal',
                callback: () => navigate(`/cards/${card.id}/verify`),
            });
        }
        actionViews.push({
            icon: 'delete',
            label: 'Delete',
            color: 'danger',
            callback: () => {
                setShowingMenu.off();
                setApproveCardDeleteOpen.on();
            },
        });
        return actionViews;
    };

    return (
        <Fragment>
            <button type='button' onClick={setShowingMenu.on} className={cls.button}>
                <img src={iconSrc} className={'size-9'} />
                <div className={cls.textContent}>
                    <div className={'fv-text-xl-regular'}>{title}</div>
                    {statusText && <div className={cls.status({color: statusColor})}>{statusText}</div>}
                </div>
                <InlineSvg src={arrowRightIcon} className={cls.arrow} />
            </button>

            <RsnDialog.Controller isOpen={showingMenu} onClose={setShowingMenu.off}>
                <RsnDialog.Window>
                    <RsnDialog.LeftHeader text={title} />
                    <RsnDialog.Body flex>
                        {getCardActionView(props).map(({callback, color, label, icon}) => (
                            <RsnIconWidget.Md
                                key={label}
                                icon={actionIconMap[icon]}
                                addon={<img src={rightArrow} />}
                                onClick={callback}
                            >
                                <RsnIconWidget.Text className={cls.action({color})}>{label}</RsnIconWidget.Text>
                            </RsnIconWidget.Md>
                        ))}
                    </RsnDialog.Body>
                </RsnDialog.Window>
            </RsnDialog.Controller>

            <RsnDialog.Controller isOpen={isApproveCardDeleteOpen} onClose={setApproveCardDeleteOpen.off}>
                <ApproveCardDeleteModal cardId={props.id} onClose={setApproveCardDeleteOpen.off} />
            </RsnDialog.Controller>
        </Fragment>
    );
}

CardListItem.Skeleton = function CardListItemSkeleton() {
    return (
        <div className={cls.button}>
            <RsnSkeleton circle width={36} height={36} />
            <div className={cn(cls.textContent, 'flex flex-col')}>
                <div className={cn(cls.centerY, 'h-5')}>
                    <RsnSkeleton width={46} height={16} />
                </div>
                <div className={cn(cls.centerY, 'h-4')}>
                    <RsnSkeleton width={80} height={12} />
                </div>
            </div>
        </div>
    );
};

export const actionIconMap = {
    delete: RsnIconWidget.framedIcon(trashBoxIcon),
    verify: {src: sheetIcon},
};

export const actionClasses = cva('', {
    variants: {
        color: {
            normal: 'text-neutral-100',
            danger: 'text-danger-30',
        },
    },
});

export const statusClasses = cva('fv-text-m-regular', {
    variants: {
        color: {
            default: 'text-neutral-90',
            danger: 'text-danger-30',
            warning: 'text-warning-30',
        },
    },
});

const cls = styles({
    status: statusClasses,
    action: actionClasses,
    button: 'flex w-full cursor-pointer items-center gap-4 p-4 text-left text-neutral-100',
    textContent: 'flex-1',
    arrow: 'size-5 text-neutral-100',
    centerY: 'flex flex-col justify-center',
});
